<script lang="ts">
  import { createEventDispatcher } from 'svelte'
  import Portal from 'svelte-portal'
  import { Modal } from '@factry/components'
  import { MeasurementStatus } from '../types'
  import { roundNumber } from '../utils/datasets'

  export let description: string = ''
  export let label: string = 'Unlabeled'
  export let max: number
  export let maxAcceptable: number
  export let mean: number
  export let min: number
  export let minAcceptable: number
  export let status: MeasurementStatus = MeasurementStatus.Passed
  export let UOM: string = ''

  const dispatch = createEventDispatcher()

  function onCancel (): void {
    dispatch('close')
  }
</script>

<style lang="scss">
  @import '../utils/base';

  .wrapper {
    display: grid;
    grid-gap: $spacing * 0.5;
    grid-template-columns: repeat(2, 1fr);
  }

  .group {
    border: 1px solid $border;
    border-radius: $radius;
    padding: $spacing * 0.5;

    &::before {
      -webkit-font-smoothing: antialiased;
      font: var(--fa-font-solid);
      text-rendering: auto;
    }

    &.is-wide {
      grid-column-start: span 2;
    }

    &.no-styling {
      border: 0;
      padding: 0;
    }

    &.has-passed {
      background-color: $primary-light;
      border-color: $primary;
      padding-left: $spacing * 1.5;
      position: relative;

      &::before {
        color: $primary-dark;
        content: '\f46c';
        display: inline-block;
        left: $spacing * 0.5;
        position: absolute;
        top: $spacing * 0.5;
      }

      .title {
        color: $primary-dark;
      }
    }

    &.has-failed {
      background-color: $danger-light;
      border-color: $danger;
      padding-left: $spacing * 1.5;
      position: relative;

      &::before {
        color: $danger-dark;
        content: '\e4e3';
        display: inline-block;
        left: $spacing * 0.5;
        position: absolute;
        top: $spacing * 0.5;
      }

      .title {
        color: $danger-dark;
      }
    }
  }

  .title {
    margin-bottom: $spacing * 0.25;
  }

  .description {
    color: $grey;
    font-size: $size-5;

    b {
      color: $grey-darker;
    }
  }

  .value {
    color: $grey-darker;
    font-size: $size-medium;
    font-weight: $weight-medium;
  }
</style>

<Portal>
  <Modal
    actionCancel
    open
    title={label}
    on:cancel={onCancel}
  >
    <div class="wrapper">
      {#if description}
        <div class="group no-styling is-wide">
          {description}
        </div>
      {/if}
      <div
        class="group is-wide"
        class:has-passed={status === MeasurementStatus.Passed}
        class:has-failed={status === MeasurementStatus.Failed}
      >
        <h3 class="title is-4">Test {status}</h3>
        <div class="description">
          {#if status === MeasurementStatus.Passed}
            The measured value of <b>{roundNumber(mean)} {UOM}</b> falls within the acceptable range of <b>{roundNumber(min)} - {roundNumber(max)} {UOM}</b>, meeting all quality standards and specifications.
          {:else if mean < min || mean > max}
            The measured value of <b>{roundNumber(mean)} {UOM}</b> falls outside of the acceptable range of <b>{roundNumber(min)} - {roundNumber(max)} {UOM}</b>, and does not meet the quality standards and specifications.
          {:else}
            The measured value of <b>{roundNumber(mean)} {UOM}</b> falls within the acceptable range of <b>{roundNumber(min)} - {roundNumber(max)} {UOM}</b>, but some values go over the acceptable maximum or under the acceptable minimum, thus it does not meet the quality standards and specifications.
          {/if}
        </div>
      </div>
      <div class="group">
        <div class="description">Minimum acceptable</div>
        <div class="value">{roundNumber(minAcceptable)} {UOM}</div>
      </div>
      <div class="group">
        <div class="description">Maximum acceptable</div>
        <div class="value">{roundNumber(maxAcceptable)} {UOM}</div>
      </div>
      <div class="group">
        <div class="description">Minimum value</div>
        <div class="value">{roundNumber(min)} {UOM}</div>
      </div>
      <div class="group">
        <div class="description">Maximum value</div>
        <div class="value">{roundNumber(max)} {UOM}</div>
      </div>
      <div class="group">
        <div class="description">Average value</div>
        <div class="value">{roundNumber(mean)} {UOM}</div>
      </div>
    </div>
  </Modal>
</Portal>
